/**
 * Answers Unisol has approved for reuse.
 *
 * The assistant reads these alongside the guides, so a question answered once
 * stops coming back as a ticket. Curated on purpose — a support reply often
 * names one workshop's own invoice, and these are read by all of them.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BrainCircuit, Loader2, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';

export const SavedAnswers = ({ refreshKey = 0 }) => {
  const { authHeader } = useAuth();
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState('');
  const [draft, setDraft] = useState({ question: '', answer: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/platform/answers`, { headers: authHeader() });
      setAnswers(data.answers || []);
    } catch { setAnswers([]); } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const save = async (id) => {
    try {
      await axios.patch(`${API}/platform/answers/${id}`, draft, { headers: authHeader() });
      setEditing('');
      await load();
      toast.success('Answer updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save that'); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/platform/answers/${id}`, { headers: authHeader() });
      setAnswers((a) => a.filter((x) => x.id !== id));
      toast.success('Removed from the assistant');
    } catch { toast.error('Could not remove that'); }
  };

  return (
    <section className="mt-10" data-testid="saved-answers">
      <h2 className="text-base font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
        <BrainCircuit size={15} className="text-teal-600" /> Answers the assistant reuses
      </h2>
      <p className="text-xs text-slate-500 mt-0.5">
        {answers.length} saved. Tick &ldquo;teach the assistant&rdquo; when you
        reply to a ticket to add one. Written in general terms, never naming one
        workshop&rsquo;s own figures.
      </p>

      {loading ? (
        <div className="p-8 grid place-items-center"><Loader2 className="animate-spin text-slate-300" size={18} /></div>
      ) : (
        <div className="mt-3 bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {answers.map((a) => (
            <div key={a.id} className="px-4 py-3" data-testid={`saved-answer-${a.id}`}>
              {editing === a.id ? (
                <div className="space-y-2">
                  <input
                    value={draft.question}
                    onChange={(e) => setDraft((d) => ({ ...d, question: e.target.value }))}
                    className="w-full text-sm font-medium border border-slate-300 rounded-lg px-2.5 py-1.5"
                    data-testid={`saved-answer-question-${a.id}`}
                  />
                  <textarea
                    rows={4}
                    value={draft.answer}
                    onChange={(e) => setDraft((d) => ({ ...d, answer: e.target.value }))}
                    className="w-full text-sm border border-slate-300 rounded-lg px-2.5 py-1.5 resize-none"
                    data-testid={`saved-answer-text-${a.id}`}
                  />
                  <div className="flex gap-2">
                    <button onClick={() => save(a.id)} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-teal-500 hover:bg-teal-600 text-white rounded-lg px-3 py-1.5" data-testid={`saved-answer-save-${a.id}`}>
                      <Check size={12} /> Save
                    </button>
                    <button onClick={() => setEditing('')} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 text-slate-600 rounded-lg px-3 py-1.5">
                      <X size={12} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900">{a.question}</p>
                    <p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">{a.answer}</p>
                    <p className="text-[10px] text-slate-400 mt-1.5">
                      {a.source_reference ? `From ${a.source_reference} · ` : ''}
                      {new Date(a.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="shrink-0 flex gap-2">
                    <button
                      onClick={() => { setEditing(a.id); setDraft({ question: a.question, answer: a.answer }); }}
                      title="Edit"
                      className="text-slate-300 hover:text-slate-600"
                      data-testid={`saved-answer-edit-${a.id}`}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => remove(a.id)}
                      title="Remove"
                      className="text-slate-300 hover:text-red-600"
                      data-testid={`saved-answer-delete-${a.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {answers.length === 0 && (
            <p className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="saved-answers-empty">
              Nothing saved yet. The next ticket you answer can be the first.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default SavedAnswers;
